import { FeatureFlagName, UserRole } from '@/types/models';
import { holdsEffectiveFlag } from '@/utils/feature-flags';

/**
 * The subject the rule reads. Structural so a session user, a D1 row and a test literal all
 * satisfy it. `flags` takes BOTH shapes in circulation — the client's `Record<string, boolean>`
 * from `/me/feature-flags` and the server's `string[]` from `loadUserFlags` — for the same
 * reason `AuthUserLike` in api/permissions.ts does.
 */
export interface IntakeQueueAccessSubject {
	role?: string | null;
	flags?: Record<string, boolean> | readonly string[] | null;
}

const holds = (flags: IntakeQueueAccessSubject['flags'], flag: string): boolean => {
	if (!flags) return false;
	return Array.isArray(flags) ? flags.includes(flag) : !!(flags as Record<string, boolean>)[flag];
};

/**
 * May this user see the INTAKE QUEUES at all — Ownership Transfers and Needs Review
 * (review requests): the pages, the nav rows, their badges, and every
 * `/accounting/ownership-transfers` and `/accounting/review-requests` route, reads included?
 *
 * The Admin role, on its own (it bypasses every gate, flags included — #627). Otherwise the
 * Accounting role or the `accounting_admin` capability, and in both cases the `accounting`
 * area flag too, because `ensureAccountingFlag` 404s both route families without it. A
 * property manager holding only
 * `accounting` is refused: resale packets — and the agent escalations filed about them —
 * carry seller/buyer names and addresses across the whole portfolio, and the queues are
 * accounting's work. Deciding a review request ALSO needs the kind's own registry permission
 * (`api/review-requests/registry.ts`); this only decides who may see the queue.
 *
 * NOT `role === Admin` alone. Full-admin staff can be property managers or accounting by role
 * and hold capability FLAGS instead, so `accounting_admin` admits any role — except a
 * contractor, who holds no capability (the grant paths refuse it; this holds if one slips).
 *
 * ONE predicate for both sides: the API gates (api/accounting/ownership-transfers.ts,
 * api/review-requests/index.ts), the nav gate + badge fetches
 * (components/layout/custom-drawer-content.tsx) and the page guards (app/ownership-transfers/,
 * app/review-requests/) all call this, so a row cannot lead to a 404.
 *
 * @param user - role + enabled flags; null/undefined (a half-loaded session) is refused.
 */
export const canAccessIntakeQueues = (user: IntakeQueueAccessSubject | null | undefined): boolean => {
	if (!user) return false;
	if (!holdsEffectiveFlag(user.role, user.flags, FeatureFlagName.Accounting)) return false;
	if (user.role === UserRole.Admin || user.role === UserRole.Accounting) return true;
	if (user.role === UserRole.Contractor) return false;
	return holds(user.flags, FeatureFlagName.AccountingAdmin);
};
